import React, { useEffect, useState } from 'react';
import { ColorPicker, Flex, Form, message, Divider, Typography } from 'antd';
import { CreateTeam, UpdateTeam } from '../../apis/settings';
import { GetTeamGroups } from '../../apis/teamGroups';
import type {
  CreateTeamWithGroupModel,
  TeamGroup,
} from '../../apis/teamGroups';
import CustomSelect from '@/components/CustomSelect';
import { capitalize, useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import { CustomInput, CustomShipmentText, CustomTitle } from '../../components';
import CustomButton from '../../components/CustomButton.jsx';
import { MailIcon } from '@/assets/icons/icons';
import SlackLogo from './SlackLogo';
import { ApiResponse } from '@/apis/types';
import theme from '@/assets/theme';
import SlackConnectionPanel from './SlackConnectionPanel';
import TeamsConnectionPanel from './TeamsConnectionPanel';
import './TeamCommunication.scss';

interface TeamData {
  id: string;
  name: string;
  color: string;
  general_support_email?: string | null;
  slack_webhook?: string | null;
  slack_connected?: boolean;
  teams_webhook?: string | null;
  group_id?: string | null;
  group_name?: string | null;
}

interface TeamFormProps {
  teamData?: TeamData;
  onClose: () => void;
  refreshTeams: () => void;
  canManageGroups?: boolean;
}

interface FormValues {
  name: string;
  general_support_email?: string;
  teams_webhook?: string;
  group_id?: string;
}

/**
 * Team creation and editing form component
 *
 * Handles creating new teams or updating existing teams with name and color.
 */
const TeamForm: React.FC<TeamFormProps> = ({
  teamData,
  onClose,
  refreshTeams,
  canManageGroups = false,
}) => {
  const [form] = Form.useForm();
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const isEditing = !!teamData;
  const [color, setColor] = useState(teamData?.color || '#000000');
  const [colorPicked, setColorPicked] = useState(false);
  const isDefaultTeam = teamData?.name?.toLowerCase() === 'default';
  const showGroupField = canManageGroups && !isDefaultTeam;
  const [groups, setGroups] = useState<TeamGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const labeltext = theme.themeType === 2 ? 'client' : 'team';

  useEffect(() => {
    if (!showGroupField || !token) return;
    let cancelled = false;
    setGroupsLoading(true);
    GetTeamGroups(token)
      .then((res) => {
        const list = res?.data?.data?.data;
        if (!cancelled && Array.isArray(list)) {
          setGroups(list);
        }
      })
      .catch((error: unknown) => {
        console.error('Error fetching groups:', error);
        if (!cancelled) message.error("Couldn't load groups");
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showGroupField, token]);

  useEffect(() => {
    if (teamData) {
      form.setFieldsValue({
        name: teamData.name,
        general_support_email: teamData.general_support_email || undefined,
        teams_webhook: teamData.teams_webhook || undefined,
        group_id: teamData.group_id || undefined,
      });

      setColor(teamData.color || '#000000');
    } else {
      form.resetFields();
      setColor('#000000');
    }
  }, [teamData, form]);
  // The current group comes from the row itself, so the select shows its name
  // even before (or if) the group list fails to load.
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: group.name,
  }));
  if (
    teamData?.group_id &&
    !groupOptions.some((option) => option.value === teamData.group_id)
  ) {
    groupOptions.unshift({
      value: teamData.group_id,
      label: teamData.group_name ?? '',
    });
  }

  // For a new team whose color the user hasn't chosen yet, picking a group
  // suggests the group's color; an existing team keeps its own color.
  const handleGroupChange = (value?: string) => {
    if (!value || teamData || colorPicked) return;
    const group = groups.find((item) => item.id === value);
    if (group?.color) setColor(group.color);
  };

  const onFinish = async (values: FormValues) => {
    if (!token) {
      message.error('Sign in again before saving the team.');
      return;
    }
    const newTeam: CreateTeamWithGroupModel = {
      name: values.name,
      general_support_email: values.general_support_email,
      teams_webhook: values.teams_webhook,
      color,
      // Only default admins may change a team's group; null takes it out
      // (and saving the Default team takes it out of any group it's in).
      ...(canManageGroups
        ? { group_id: (!isDefaultTeam && values.group_id) || null }
        : {}),
    };
    showSpin();
    try {
      let response: ApiResponse;
      if (isEditing) {
        const model = { ...newTeam, id: teamData.id };
        response = await UpdateTeam(token, model);
      } else {
        response = await CreateTeam(token, newTeam);
      }
      if (response?.data?.data?.is_success) {
        message.success(
          `${capitalize(labeltext)}  ${isEditing ? 'updated' : 'added'} successfully!`
        );
        refreshTeams();
        form.resetFields();
        setColor('#000000');
        onClose();
      } else {
        message.error(
          response?.data?.data?.message || `Failed to save ${labeltext}`
        );
      }
    } catch (error) {
      console.error('API Error:', error);
    } finally {
      hideSpin();
    }
  };

  return (
    <Form
      form={form}
      layout='vertical'
      requiredMark={false}
      onFinish={onFinish}
      initialValues={{ group_id: teamData?.group_id || undefined }}
      className='team-settings-form'
    >
      <Flex vertical gap={24} className='team-form__content'>
        <CustomTitle
          text={isEditing ? `Edit ${labeltext}` : `Add ${labeltext}`}
          fontSize={24}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
        <Form.Item
          label='Name'
          name='name'
          className='custom-input form-input-style'
          rules={[
            { required: true, message: `Please enter a ${labeltext} name` },
            {
              max: 50,
              message: `${capitalize(labeltext)} name cannot exceed 50 characters`,
            },
          ]}
        >
          <CustomInput placeholder={`Enter ${labeltext} name`} />
        </Form.Item>
        {showGroupField && (
          <Form.Item
            label='Group'
            name='group_id'
            className='custom-input form-input-style'
          >
            <CustomSelect
              bgColor='var(--light-gray)'
              textColor='var(--text-primary)'
              height='36px'
              borderRadius='8px'
              border='none'
              loading={groupsLoading}
              placeholder='Select'
              allowClear
              onChange={handleGroupChange}
              options={groupOptions}
            />
          </Form.Item>
        )}
        <Form.Item className='custom-input form-input-style' label='Color'>
          <Flex gap={16} align='center'>
            <ColorPicker
              value={color}
              showText
              format='hex'
              onChange={(newColor) => {
                setColorPicked(true);
                setColor(newColor.toHexString());
              }}
            />
            <Flex
              aria-hidden='true'
              className='team-form__color-preview'
              style={{ backgroundColor: color }}
            >
              <span />
            </Flex>
          </Flex>
        </Form.Item>
        <Divider className='team-form__divider' />
        <Flex vertical gap={24}>
          <Flex vertical gap={4}>
            <CustomShipmentText
              text={`${capitalize(labeltext)} communication`}
              fontSize='18px'
              fontWeight={700}
              lineHeight='24px'
            />
            <CustomShipmentText
              text={`Specify channels for this ${labeltext}`}
              fontSize='14px'
              lineHeight='20px'
              color='var(--text-secondary)'
            />
          </Flex>
          <Flex vertical gap={12} className='team-communication'>
            <Flex
              gap={12}
              align='flex-start'
              className='team-communication__card'
            >
              <span className='team-communication__icon'>
                <MailIcon width={16} height={16} />
              </span>
              <Flex vertical gap={8} className='team-communication__content'>
                <Typography.Text strong>Email</Typography.Text>
                <Typography.Text className='team-communication__hint'>
                  Send notifications to a shared inbox
                </Typography.Text>
                <Form.Item
                  name='general_support_email'
                  className='custom-input form-input-style'
                  rules={[
                    {
                      type: 'email',
                      message: 'Please enter a valid email address',
                    },
                  ]}
                >
                  <CustomInput
                    aria-label='Team email'
                    placeholder='e.g. support@company.com'
                  />
                </Form.Item>
              </Flex>
            </Flex>
            <Form.Item
              name='teams_webhook'
              hidden
              className='fs-exclude rr-block rr-ignore'
            >
              <CustomInput
                readOnly
                autoComplete='off'
                className='statsig-no-capture'
                classNames={{ input: 'statsig-no-capture' }}
              />
            </Form.Item>
            <TeamsConnectionPanel
              teamId={teamData?.id}
              token={token || undefined}
              initialWebhook={teamData?.teams_webhook || ''}
              onConnected={(url) => form.setFieldsValue({ teams_webhook: url })}
            />
            {teamData && token ? (
              <SlackConnectionPanel
                teamId={teamData.id}
                token={token}
                onChange={refreshTeams}
              />
            ) : (
              <Flex
                gap={12}
                align='center'
                className='team-communication__card'
              >
                <span className='team-communication__icon'>
                  <SlackLogo />
                </span>
                <Flex vertical className='team-communication__content'>
                  <Typography.Text strong>Slack</Typography.Text>
                  <Typography.Text className='team-communication__hint'>
                    Save the {labeltext} before connecting Slack.
                  </Typography.Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex vertical gap={16} className='team-form__footer'>
        <CustomButton
          text='Save'
          bgColor='var(--text-primary)'
          htmlType='submit'
          color='var(--surface-container)'
          fontSize='16px'
          fontWeight={600}
          borderRadius='8px'
          boxShadow='none'
          height={48}
          block
        />
        <CustomButton
          text='Cancel'
          bgColor='transparent'
          color='var(--text-primary)'
          fontSize='16px'
          fontWeight={500}
          boxShadow='none'
          height={44}
          block
          padding='10px 0'
          border='none'
          onClick={onClose}
        />
      </Flex>
    </Form>
  );
};

export default TeamForm;
